import React, { useCallback, useEffect, useRef, useState } from 'react';
import './PrinterDevicePanel.css';
import PrinterDetails from '../PrinterDetails';
import SuppliesComponent from '../SuppliesComponent';
import { ReactComponent as CollapseIcon } from '../../../../assets/images/mydevices/panelCollapseIcon.svg';
import printerIcon from '../../../../assets/images/mydevices/printIcon.svg';
import BenefitCard from '../BenefitsCard/BenefitCard';
import addSubscriptionIcon from "../../../../assets/images/mydevices/addsubscription-logo.svg";
import brotherConnectIcon from "../../../../assets/images/mydevices/brother-connect-logo.svg";
import BlockHeader from '../BlockHeader/BlockHeader';
import { useTranslation } from 'react-i18next';
import {
    DEVICE_ACTIONS,
    UPGRADE_TO_ECOPRO,
    USER_ENROLLED_SERVICE_LIST,
    LOCALSESSIONDATAKEYS,
    featureSwitchName,
    FAILURE
} from '../../../../constants/AppConstants';
import EcoproDashboardAlert from '../../../../components/CustomerDashboard/EcoPro/EcoproDashboardAlert/EcoproDashboardAlert';
import OrderHistoryWidget from '../OrderHistoryWidget/OrderHistoryWidget';
import EasyBuyComponent from '../EasyBuyComponent/EasyBuyComponent';
import { getFeatureSwitchByKey, getLocalSessionData } from '../../../../utils/Authentication/handleUserSession';
import BPlusNewDevicePromotionBanner from '../BPlusNewDevicePromotionBanner/BPlusNewDevicePromotionBanner';
import sparkleIcon from "../../../../assets/images/BrotherPlus/wand-magic-sparkles-icon.svg";
import InkLevels from '../EasyBuyComponent/InkLevels/InkLevels';
import SectionHeader from '../SectionHeader/SectionHeader';
import SvgOrderHistory from '../../../../components/SvgContainer/SvgIcons/SvgOrderHistory';
import { useSelector } from 'react-redux';
import EcoproAlert from '../../../../components/shared/alert/alert';

const PrinterDevicePanel = ({ deviceData, openEasyBuy = false, easyBuyRef, scrollOnMount = false, onEasyBuyScrolled }) => {
    const { t } = useTranslation(["translation"]);
    const getDevicesListResp = useSelector((state => state.BROTHERPLUS.getDevices));
    const orderHistoryLogo = <SvgOrderHistory width={20} height={20} color='#11339D' ></SvgOrderHistory>
    const selectedDeviceId = getLocalSessionData(LOCALSESSIONDATAKEYS.DEVICE_ID);
    const [expand, setExpand] = useState(true);
    const [deviceInfoDetails, setDeviceInfoDetails] = useState(null);
    const [deviceStatusDetails, setDeviceStatusDetails] = useState(null);
    const [isBplusDeviceSelected, setIsBplusDeviceSelected] = useState(false);
    const [isNewPrinter, setIsNewPrinter] = useState(false);
    const [isApplyBPlusNewDeviceDiscount, setIsApplyBPlusNewDeviceDiscount] = useState(false);
    const panelContentRef = useRef(null);
    const [isBPlusUpgradedEcoPro, setIsBPlusUpgradedEcoPro] = useState(false);
    const BrotherPlusProgramFeatureSwitchEnabled = getFeatureSwitchByKey(featureSwitchName.BROTHER_PLUS_NEWDEVICE_PROMO_CODE)

    function handlebuttonChnage() {
        setExpand(!expand)
    }

    useEffect(() => {
        setDeviceInfoDetails(null);
        setDeviceStatusDetails(null);
    }, [deviceData?.deviceId]);

    const handleDeviceInfoDetails = useCallback((deviceInfoData) => {
        if (deviceInfoData?.response?.deviceId &&
            deviceInfoData?.response?.deviceId !== getLocalSessionData(LOCALSESSIONDATAKEYS.DEVICE_ID)) {
            return;
        }
        setDeviceInfoDetails(prev =>
            prev === deviceInfoData ? prev : deviceInfoData
        );
    }, []);

    const handleDeviceStatusDetails = useCallback((deviceStatusData) => {
        // Ignore response if deviceId doesn't match currently selected device
        if (deviceStatusData?.response?.deviceId &&
            deviceStatusData?.response?.deviceId !== getLocalSessionData(LOCALSESSIONDATAKEYS.DEVICE_ID)) {
            return;
        }
        setDeviceStatusDetails(prev =>
            prev === deviceStatusData ? prev : deviceStatusData
        );
    }, []);

    useEffect(() => {
        if (getDevicesListResp?.devices?.devices?.length > 0) {
            const deviceList = getDevicesListResp?.devices?.devices;
            let serviceList = [];

            deviceList.forEach((device) => {
                if (device?.deviceId === selectedDeviceId) {
                    if (device?.serviceStatus?.length > 1) {
                        device?.serviceStatus.forEach(serviceState => serviceList.push(serviceState.serviceName))
                    }
                    const isMultiEnrolled =
                        serviceList.includes(USER_ENROLLED_SERVICE_LIST.BPLUS) &&
                        serviceList.includes(USER_ENROLLED_SERVICE_LIST.ECOPRO);
                    setIsBPlusUpgradedEcoPro(isMultiEnrolled);
                }
            })
        }
    }, [getDevicesListResp, getDevicesListResp?.devices, selectedDeviceId])

    const handleNewDevicePromotionBannerClick = () => {
        if (!isApplyBPlusNewDeviceDiscount) {
            setIsApplyBPlusNewDeviceDiscount(true);
        }
    };

    const ecoProService = deviceData?.services?.ecopro;
    const bPlusService = deviceData?.services?.brother;

    const isEcoProSuccess = deviceData?.services?.ecopro?.isSuccess === true;
    const isBrotherSuccess = deviceData?.services?.brother?.isSuccess === true;

    const isEcoProEnrolled = !!ecoProService;
    const isBPlusEnrolled = !!bPlusService;

    const isEcoproCancelled = deviceInfoDetails?.response?.service === null && deviceData?.services?.ecopro?.isSuccess === true;

    const isBplusCancelled = deviceStatusDetails?.response?.actionStatus?.toUpperCase() === FAILURE;

    const isEnrolledToMultipleServices = isEcoProSuccess && isBrotherSuccess;

    const isBothCancelled = isEcoproCancelled && isBplusCancelled;

    const isOnlyEcoCancelled = isEcoproCancelled && !isBplusCancelled;
    const isOnlyBplusCancelled = isBplusCancelled && !isEcoproCancelled;

    const isSingleService = !isEnrolledToMultipleServices;

    const shouldShowAlert = isBothCancelled || (isSingleService && (isOnlyEcoCancelled || isOnlyBplusCancelled));

    const showEcoWidget = isEcoProSuccess && !isEcoproCancelled;
    const showBplusWidget = isBPlusEnrolled && !isBplusCancelled;

    useEffect(() => {
        const isBplusDevice = deviceStatusDetails?.success
            && getLocalSessionData(LOCALSESSIONDATAKEYS.DEVICE_ID) === deviceStatusDetails?.response?.deviceId
            && !isEcoProEnrolled
        setIsBplusDeviceSelected(isBplusDevice);
        // get new printer status from GetDeviceStatus data
        setIsNewPrinter((deviceStatusDetails?.success && deviceStatusDetails?.response?.newPrinter === "new") ? true : false);
        // eslint-disable-next-line
    }, [deviceStatusDetails]);


    const benefitCards = [
        showEcoWidget
            ? {
                title: t('MY_DEVICES.HEADER.CHANGE_PLAN'),
                description: t('MY_DEVICES.HEADER.ECOPRO_ENROLLED'),
                headerIcon: null,
                bgColor: "#E9D4E9",
                cursor: 'pointer',
                action: DEVICE_ACTIONS.CHANGE_PLAN,
                cname: 'change-plan-benefit-card'
            }
            : {
                title: t('MY_DEVICES.HEADER.ADD_SUBSCRIPTION'),
                description: t('MY_DEVICES.HEADER.SAVE_MONEY'),
                headerIcon: addSubscriptionIcon,
                bgColor: "#FCDFDF",
                cursor: "pointer",
                action: UPGRADE_TO_ECOPRO,
                cname: 'upgrade-to-ecopro-benefit-card'
            },
        {
            title: t('MY_DEVICES.HEADER.CONNECT_BENEFITS'),
            description: t('MY_DEVICES.HEADER.EXPLORE_BENEFITS'),
            headerIcon: brotherConnectIcon,
            bgColor: "#ECF2FF",
            action: DEVICE_ACTIONS.CONNECT_BENEFITS,
            cname: 'connect-benefit-card'
        }
    ];

    return (
        <div className="printer-device-panel">
            {shouldShowAlert && (
                <EcoproAlert
                    alertSubHeader={t("BROTHER_PLUS.CANCEL_PLAN.CONFIRMED_CANCELLATION")}
                />
            )}
            {!shouldShowAlert && (
                <>
                    <div className="printer-device-header printer-device-header-desktop-view">
                        <div className='content-wrapper'>
                            <BlockHeader
                                icon={printerIcon}
                                iconTitle="printer-icon"
                                iconAlt="printer-icon"
                                title={deviceData.deviceName || deviceData.model}
                            />
                            <button onClick={handlebuttonChnage} className="add-button">{expand ? <CollapseIcon /> : "+"}</button>
                        </div>
                    </div>
                    <div className='printer-device-header-mobile-view'>
                        <div className='content-wraper'>
                            <span>{deviceData.deviceName || deviceData.model}</span>
                            <button onClick={handlebuttonChnage} className="add-button">{expand ? <CollapseIcon /> : "+"}</button>
                        </div>
                    </div>

                    <hr className="header-divider" />

                    {
                        // In Multiple devices, alert messages will be diaplyed olny for EcoPro devices.
                        // alert messages to BrotherPlus devices will be included in future scope.
                        // display alert message only when device panel is expanded
                        (deviceInfoDetails?.success && showEcoWidget && expand)
                            ? <div className='device-alert-header'><EcoproDashboardAlert deviceInfoResponse={deviceInfoDetails} /></div>
                            : null
                    }

                    {expand &&
                        <div className='panel-accordian-content-wrapper' ref={panelContentRef} >
                            {
                                (Boolean(BrotherPlusProgramFeatureSwitchEnabled) && isBplusDeviceSelected && isNewPrinter && showBplusWidget) &&
                                <BPlusNewDevicePromotionBanner onNewDevicePromotionBannerClick={handleNewDevicePromotionBannerClick} />
                            }
                            {!shouldShowAlert && (
                                <div className="panel-content">
                                    <div className='left-section'>
                                        <PrinterDetails deviceData={deviceData} deviceInfoResponse={deviceInfoDetails} deviceStatusResponse={deviceStatusDetails} />
                                    </div>
                                    <div className="right-section">
                                        {benefitCards.map((card, index) => (
                                            <BenefitCard key={index} {...card} />
                                        ))}
                                    </div>
                                </div>
                            )}
                            {deviceStatusDetails?.success && showBplusWidget && isBPlusUpgradedEcoPro && (selectedDeviceId === deviceStatusDetails?.response?.deviceId) &&
                                <>
                                    <SectionHeader imageSrc={sparkleIcon} title={t("INK_LEVELS.TITLE")} svgIcon={false} />
                                    <InkLevels getDeviceStatusResponse={deviceStatusDetails} isBPlusUpgradedEcoPro={isBPlusUpgradedEcoPro} />

                                </>
                            }
                            {deviceStatusDetails?.success && showBplusWidget && !isBPlusUpgradedEcoPro && (selectedDeviceId === deviceStatusDetails?.response?.deviceId) &&
                                <div className="easy-buy-component-wrapper">
                                    <EasyBuyComponent
                                        newPrinter={isNewPrinter}
                                        getDeviceStatusResponse={deviceStatusDetails}
                                        isApplyBPlusNewDeviceDiscount={isApplyBPlusNewDeviceDiscount}
                                        defaultExpanded={openEasyBuy}
                                        easyBuyRef={easyBuyRef}
                                        scrollOnMount={scrollOnMount}
                                        onEasyBuyScrolled={onEasyBuyScrolled}
                                    />
                                </div>
                            }
                            {
                                (showEcoWidget) &&
                                <>
                                    <SectionHeader imageSrc={orderHistoryLogo} title={t("ORDERHISTORY.TITLE")} svgIcon={true} />
                                    <OrderHistoryWidget />
                                </>
                            }
                            {showEcoWidget || showBplusWidget ?
                                <SuppliesComponent
                                    deviceData={deviceData}
                                    handleDeviceInfoDetails={handleDeviceInfoDetails}
                                    handleDeviceStatusDetails={handleDeviceStatusDetails}
                                    isBPlusUpgradedEcoPro={isBPlusUpgradedEcoPro}
                                    showEcoWidget={showEcoWidget}
                                    showBplusWidget={showBplusWidget}
                                    isSingleService={isSingleService}
                                    isOnlyEcoCancelled={isOnlyEcoCancelled}
                                /> : null}
                        </div>
                    }
                </>)}
        </div>
    );
};

export default PrinterDevicePanel;